/**
 * secureProtocol.js
 * Utility to enforce HTTPS in production environments
 * and manage protocol-related requirements
 */

import debug from './debug';

/**
 * Checks if the current connection is secure (HTTPS)
 * @returns {boolean} True if the connection is HTTPS
 */
export const isSecureConnection = () => {
  return window.location.protocol === 'https:';
};

/**
 * Checks if the app is running in a development environment
 * @returns {boolean} True if in development environment
 */
export const isDevelopmentEnvironment = () => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Checks if the current environment requires HTTPS
 * @returns {boolean} True if HTTPS is required
 */
export const isHttpsRequired = () => {
  return !isDevelopmentEnvironment();
};

/**
 * Enforces HTTPS by redirecting to HTTPS if needed
 * Only redirects in production environments
 * @returns {boolean} True if redirect was performed
 */
export const enforceHttps = () => {
  if (isHttpsRequired() && !isSecureConnection()) {
    const httpsUrl = window.location.href.replace('http:', 'https:');
    debug.log('Security', 'Redirecting to HTTPS', httpsUrl);
    window.location.href = httpsUrl;
    return true;
  }
  return false;
};

/**
 * Logs a warning if HTTP is being used in development
 * for connections that should use HTTPS in production
 * @param {string} feature - The name of the feature requiring HTTPS
 */
export const warnHttpsInDevelopment = (feature) => {
  if (isDevelopmentEnvironment() && !isSecureConnection()) {
    debug.warn(
      'Security', 
      `${feature} is being loaded over HTTP. This is allowed in development, but live integrations must use HTTPS.`
    );
    console.warn(
      `${feature} is being loaded over HTTP. This is allowed in development, but live integrations must use HTTPS.`
    );
  }
};

// Fix ESLint warning by assigning the object to a variable before exporting
const secureProtocolModule = {
  isSecureConnection,
  isDevelopmentEnvironment,
  isHttpsRequired,
  enforceHttps,
  warnHttpsInDevelopment
};

export default secureProtocolModule; 